import NumberLine from '../entities/NumberLine.js';

export class UIManager {
    constructor(app) {
        this.app = app;
        this.navigation = document.getElementById('navigation');
        this.currentView = 'main'; // main, stories, toolsets, leftHemisphere, rightHemisphere
        
        // Store previous view for back navigation
        this.previousView = null;
    }

    init() {
        console.log('Initializing UI Manager');
        
        // Handle initial route
        const path = window.location.pathname;
        if (path === '/') {
            this.showMenu('main');
        } else if (path === '/stories') {
            this.showMenu('stories');
        } else if (path === '/toolsets') {
            this.showMenu('toolsets');
        } else if (path === '/toolsets/left') {
            this.showMenu('toolsets');
            // Small delay to ensure toolset menu is ready
            setTimeout(() => {
                if (this.app.toolsetManager) {
                    this.app.toolsetManager.selectLeftHemisphere({ updateHistory: false });
                }
            }, 100);
        } else if (path === '/toolsets/right') {
            this.showMenu('toolsets');
            // Small delay to ensure toolset menu is ready
            setTimeout(() => {
                if (this.app.toolsetManager) {
                    this.app.toolsetManager.selectRightHemisphere({ updateHistory: false });
                }
            }, 100);
        } else if (path.startsWith('/stories/')) {
            const [, , storyId, chapterSlug] = path.split('/');
            this.showMenu('stories');
            // Small delay to ensure story menu is ready
            setTimeout(() => {
                if (this.app.storyManager) {
                    // First show the story
                    this.app.storyManager.showStory(storyId, { updateHistory: false });
                    
                    // Then if there's a chapter slug, find and show that chapter
                    if (chapterSlug) {
                        const story = this.app.storyManager.stories[storyId];
                        if (story) {
                            // Find chapter by slug
                            const chapterIndex = story.chapters.findIndex(ch => ch.slug === chapterSlug);
                            if (chapterIndex !== -1) {
                                this.app.storyManager.showChapter(storyId, chapterIndex, { updateHistory: false });
                            }
                        }
                    }
                }
            }, 100);
        }

        // Listen for popstate (back/forward navigation)
        window.addEventListener('popstate', (event) => {
            const path = window.location.pathname;
            if (path === '/') {
                this.showMenu('main', { updateHistory: false });
            } else if (path === '/stories') {
                this.showMenu('stories', { updateHistory: false });
            } else if (path === '/toolsets') {
                this.showMenu('toolsets', { updateHistory: false });
            } else if (path === '/toolsets/left') {
                this.showMenu('toolsets', { updateHistory: false });
                if (this.app.toolsetManager) {
                    this.app.toolsetManager.selectLeftHemisphere({ updateHistory: false });
                }
            } else if (path === '/toolsets/right') {
                this.showMenu('toolsets', { updateHistory: false });
                if (this.app.toolsetManager) {
                    this.app.toolsetManager.selectRightHemisphere({ updateHistory: false });
                }
            } else if (path.startsWith('/stories/')) {
                const [, , storyId, chapterSlug] = path.split('/');
                this.showMenu('stories', { updateHistory: false });
                if (this.app.storyManager) {
                    // First show the story
                    this.app.storyManager.showStory(storyId, { updateHistory: false });
                    
                    // Then if there's a chapter slug, find and show that chapter
                    if (chapterSlug) {
                        const story = this.app.storyManager.stories[storyId];
                        if (story) {
                            // Find chapter by slug
                            const chapterIndex = story.chapters.findIndex(ch => ch.slug === chapterSlug);
                            if (chapterIndex !== -1) {
                                this.app.storyManager.showChapter(storyId, chapterIndex, { updateHistory: false });
                            }
                        }
                    }
                }
            }
        });
    }

    /**
     * Shows the appropriate menu based on the section
     * @param {string} section - The section to show ('main', 'stories', 'toolsets', 'leftHemisphere', 'rightHemisphere')
     * @param {Object} options - Additional options
     * @param {boolean} options.updatePrevious - Whether to update the previous view (default: true)
     */
    showMenu(section, options = {}) {
        console.log(`Showing ${section} menu`);
        this.clearNavigation();
        
        // Store previous view for back navigation if needed
        const { updatePrevious = true, updateHistory = true } = options;
        if (updatePrevious && this.currentView !== section) {
            this.previousView = this.currentView;
        }
        
        // Update current view
        this.currentView = section;
        
        // Update URL if needed
        if (updateHistory) {
            const path = section === 'main' ? '/' : `/${section}`;
            history.pushState({}, '', path);
        }
        
        // Update speech bubble messages for the section
        if (this.app.speechBubbleManager) {
            this.app.speechBubbleManager.setSection(section);
        }
        
        // Handle toolset visibility
        if (section === 'toolsets' || section === 'leftHemisphere' || section === 'rightHemisphere') {
            if (this.app.toolsetManager) {
                this.app.toolsetManager.show();
            }
        } else {
            if (this.app.toolsetManager) {
                this.app.toolsetManager.hide();
            }
        }
        
        // Call the appropriate menu creation method
        switch(section) {
            case 'main':
                this.createMainMenu();
                break;
            case 'stories':
                this.createStoriesMenu();
                break;
            case 'toolsets':
                this.createToolsetsMenu();
                break;
            case 'leftHemisphere':
                this.createLeftHemisphereMenu();
                break;
            case 'rightHemisphere':
                this.createRightHemisphereMenu();
                break;
            default:
                console.error(`Unknown section: ${section}`);
                this.createMainMenu(); // Fallback to main menu
        }
    }

    /**
     * Creates the main menu with Stories and Toolsets buttons
     */
    createMainMenu() {
        console.log('Creating main menu');
        
        // Create Stories button
        const storiesBtn = this.createButton('STORIES');
        storiesBtn.addEventListener('click', () => {
            console.log('Stories button clicked');
            this.showMenu('stories');
        });

        // Create Toolsets button
        const toolsetsBtn = this.createButton('TOOLSETS');
        toolsetsBtn.addEventListener('click', () => {
            console.log('Toolsets button clicked');
            this.showMenu('toolsets');
            // Pod transformation is now handled by the ToolsetManager in showMenu
        });

        // Create a group for main menu buttons
        const mainGroup = document.createElement('div');
        mainGroup.className = 'button-group';
        mainGroup.appendChild(storiesBtn);
        mainGroup.appendChild(toolsetsBtn);
        
        this.navigation.appendChild(mainGroup);
    }

    /**
     * Creates the stories menu with Briolissens and Dunyawi options
     */
    createStoriesMenu() {
        // Enable Morse code blinking when entering Stories section
        if (this.app.podManager) {
            this.app.podManager.enableMorseCode();
            
            // Change eye colors to African violet (keeping outermost layer unchanged)
            // African violet color: 0x9966CC
            if (this.app.podManager.pod) {
                // Use a special method to set the eye colors with glow for stories section
                this.app.podManager.pod.setEyeColorsForStories();
            }
        }

        // Create back button
        const backBtn = this.createBackButton(() => {
            console.log('Back to main menu');
            this.app.storyManager.hideStory();
            this.app.podManager.resetMovement();
            
            // Disable starfield movement when returning to main menu
            if (this.app.sceneManager && this.app.sceneManager.starfield) {
                this.app.sceneManager.starfield.setMoving(false);
            }
            
            // Disable Morse code blinking when exiting Stories section
            if (this.app.podManager) {
                this.app.podManager.disableMorseCode();
                
                // Reset eye colors to original blue
                if (this.app.podManager.pod) {
                    this.app.podManager.pod.resetEyeColors();
                }
            }
            
            this.showMenu('main');
        });

        // Create story options
        const briolissensBtn = this.createButton('BRIOLISSENS');
        briolissensBtn.addEventListener('click', () => {
            console.log('Starting orbital movement');
            // Ensure starfield doesn't move for Briolissens
            if (this.app.sceneManager && this.app.sceneManager.starfield) {
                this.app.sceneManager.starfield.setMoving(false);
            }
            // Start movement and show story after delay
            this.app.podManager.startOrbitalMovement(() => {
                this.app.storyManager.showStory('briolissens');
            });
        });

        const dunyawiBtn = this.createButton('DUNYAWI');
        dunyawiBtn.addEventListener('click', () => {
            console.log('Starting forward movement');
            // Enable starfield movement for Dunyawi
            if (this.app.sceneManager && this.app.sceneManager.starfield) {
                this.app.sceneManager.starfield.setMoving(true);
            }
            // Start movement and show story after delay
            this.app.podManager.startForwardMovement(() => {
                this.app.storyManager.showStory('dunyawi');
            });
        });

        // Create a container for the story selection with number line
        const storyContainer = document.createElement('div');
        storyContainer.className = 'story-selection-container';
        
        // Create the number line using the NumberLine entity
        const numberLine = new NumberLine({
            markerCount: 3,  // Only 3 markers: one on each side of zero
            labelPositions: [1],  // Only show label at the middle position
            labelFormatter: () => '0',  // Always show '0' as the label
            width: '150px'  // Half the original width
        });
        
        // Create a group for story buttons
        const storyGroup = document.createElement('div');
        storyGroup.className = 'button-group';
        
        // Add the elements in the correct order
        storyGroup.appendChild(briolissensBtn);
        storyGroup.appendChild(numberLine.getElement());
        storyGroup.appendChild(dunyawiBtn);
        
        storyContainer.appendChild(storyGroup);
        
        // Add story container and back button to navigation
        this.navigation.appendChild(storyContainer);
        this.navigation.appendChild(backBtn);
    }

    /**
     * Creates the toolsets menu with Left Hemisphere and Right Hemisphere options
     */
    createToolsetsMenu() {
        console.log('Creating toolsets menu');
        
        // Ensure the speech bubble manager is aware we're in the toolsets section
        // This ensures the TAP ME indicator appears if the user hasn't seen toolset messages
        if (this.app.speechBubbleManager) {
            // This will trigger the speech bubble system to check if the user has seen toolset messages
            // and show the TAP ME indicator if needed
            this.app.speechBubbleManager.setSection('toolsets');
        }
        
        // Create back button
        const backBtn = this.createBackButton(() => {
            console.log('Back to main menu');
            
            // Reset pod appearance
            if (this.app.podManager) {
                this.app.podManager.resetPodAppearance();
            }
            
            this.showMenu('main');
        });

        // Create hemisphere options
        const leftHemisphereBtn = this.createButton('LEFT HEMISPHERE');
        leftHemisphereBtn.addEventListener('click', () => {
            console.log('Left Hemisphere clicked');
            this.showMenu('leftHemisphere');
            
            // Notify the toolset manager about hemisphere selection
            if (this.app.toolsetManager) {
                this.app.toolsetManager.selectLeftHemisphere();
            }
        });

        const rightHemisphereBtn = this.createButton('RIGHT HEMISPHERE');
        rightHemisphereBtn.addEventListener('click', () => {
            console.log('Right Hemisphere clicked');
            this.showMenu('rightHemisphere');
            
            // Notify the toolset manager about hemisphere selection
            if (this.app.toolsetManager) {
                this.app.toolsetManager.selectRightHemisphere();
            }
        });

        // Create a group for hemisphere buttons
        const hemisphereGroup = document.createElement('div');
        hemisphereGroup.className = 'button-group';
        hemisphereGroup.appendChild(leftHemisphereBtn);
        hemisphereGroup.appendChild(rightHemisphereBtn);

        // Add hemisphere group and back button to navigation
        this.navigation.appendChild(hemisphereGroup);
        this.navigation.appendChild(backBtn);
    }

    /**
     * Creates the left hemisphere tools menu
     */
    createLeftHemisphereMenu() {
        // Create back button to return to toolsets menu
        const backBtn = this.createBackButton(() => {
            console.log('Back to toolsets menu');
            this.showMenu('toolsets');
        });

        // Placeholder for left hemisphere tools
        // This will be replaced with actual tools in the future
        const placeholderText = document.createElement('div');
        placeholderText.className = 'placeholder-text';
        placeholderText.textContent = 'Left Hemisphere Tools Coming Soon';
        
        this.navigation.appendChild(placeholderText);
        this.navigation.appendChild(backBtn);
    }

    /**
     * Creates the right hemisphere tools menu
     */
    createRightHemisphereMenu() {
        // Create back button to return to toolsets menu
        const backBtn = this.createBackButton(() => {
            console.log('Back to toolsets menu');
            // Hide any active tool window before showing toolsets
            if (this.app.toolsetManager) {
                this.app.toolsetManager.hideToolWindow();
            }
            this.showMenu('toolsets');
        });

        this.navigation.appendChild(backBtn);
    }

    /**
     * Creates a back button with the given callback
     * @param {Function} callback - The function to call when the button is clicked
     * @returns {HTMLButtonElement} The created back button
     */
    createBackButton(callback) {
        const backBtn = this.createButton('← BACK');
        backBtn.addEventListener('click', callback);
        return backBtn;
    }

    /**
     * Creates a button with the given text
     * @param {string} text - The text to display on the button
     * @returns {HTMLButtonElement} The created button
     */
    createButton(text) {
        const button = document.createElement('button');
        button.textContent = text;
        button.className = 'button';
        return button;
    }

    /**
     * Clears all elements from the navigation container
     */
    clearNavigation() {
        while (this.navigation.firstChild) {
            this.navigation.removeChild(this.navigation.firstChild);
        }
    }
}
